import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Package,
  FileText,
  Wrench,
  BookOpen,
  Shield,
  ExternalLink,
} from "lucide-react";
import type { Product } from "@/types/product";
import { formatCentsAsDollars, formatUnitOfMeasure } from "@/lib/price-utils";
import { ProductQuantityControls } from "@/components/products/ProductQuantityControls";

interface ProductDetailsDialogProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  showCachedPricing?: boolean;
  disabled?: boolean;
}

// ── Field type definitions ───────────────────────────────────────────────────

type FieldType = "text" | "number" | "url" | "list" | "image" | "hidden";

interface FieldDef {
  label: string;
  type: FieldType;
  /** Separator for list fields (default "|") */
  separator?: string;
  /** Icon component for URL fields */
  icon?: React.FC<{ className?: string }>;
}

// Map of extraData keys (Eclipse ERP column headers) to display metadata.
// Order within each type group controls display order.
const extraDataFields: Record<string, FieldDef> = {
  // ── URL fields (rendered as buttons above the grid) ──
  "Spec Sheet URL": {
    label: i18n.t("components.products.productDetailsDialog.specSheet"),
    type: "url",
    icon: FileText,
  },
  "O&M URL": {
    label: i18n.t("components.products.productDetailsDialog.oMGuide"),
    type: "url",
    icon: Wrench,
  },
  "Installation Instructions URL": {
    label: i18n.t("components.products.productDetailsDialog.installation"),
    type: "url",
    icon: BookOpen,
  },
  "Warranty URL": {
    label: i18n.t("components.products.productDetailsDialog.warranty"),
    type: "url",
    icon: Shield,
  },
  "OWNERS MANUAL": {
    label: i18n.t("components.products.productDetailsDialog.ownerSManual"),
    type: "url",
    icon: BookOpen,
  },

  // ── List fields (rendered below the grid) ──
  "Feature Bullets": {
    label: i18n.t("components.products.productDetailsDialog.features"),
    type: "list",
    separator: "|",
  },
  "Marketing Copy": {
    label: i18n.t("components.products.productDetailsDialog.marketingDetails"),
    type: "list",
    separator: "|",
  },

  // ── Text fields (rendered in the grid) ──
  "Product Select Code": { label: i18n.t("components.products.productDetailsDialog.productCategory"), type: "text" },
  "Product Class": { label: i18n.t("components.products.productDetailsDialog.productClass"), type: "text" },
  "Taxonomy Level 1": { label: i18n.t("components.products.productDetailsDialog.category"), type: "text" },
  "Taxonomy Level 2": { label: i18n.t("components.products.productDetailsDialog.subcategory"), type: "text" },
  "Taxonomy Level 3": { label: i18n.t("components.products.productDetailsDialog.productType"), type: "text" },
  "Taxonomy Level 4": { label: i18n.t("components.products.productDetailsDialog.productSubtype"), type: "text" },
  "Taxonomy Level 5": { label: i18n.t("components.products.productDetailsDialog.productGroup"), type: "text" },
  "Taxonomy Level 6": { label: i18n.t("components.products.productDetailsDialog.productDetail"), type: "text" },
  "Model Number": { label: i18n.t("components.products.productDetailsDialog.modelNumber"), type: "text" },
  "Base Model Number": { label: i18n.t("components.products.productDetailsDialog.baseModelNumber"), type: "text" },

  // Manufacturer — one of four overlapping vendor/brand fields; the others are commented below.
  "Parametric Vendor": { label: i18n.t("components.products.productDetailsDialog.manufacturer"), type: "text" },
  // "Vendor / Brand Name":           { label: "Vendor / Brand",     type: "text" },
  // "PIM Manufacturer Short Name":   { label: "Manufacturer",       type: "text" },
  // "Manufacturer Brand Short Name": { label: "Manufacturer Brand", type: "text" },

  // Trade Name — collection/product-line name; "Trade Name" is a lower-coverage duplicate.
  "WOE Trade Name": { label: i18n.t("components.products.productDetailsDialog.tradeName"), type: "text" },
  // "Trade Name": { label: "Trade Name", type: "text" },

  "Parametric Product": { label: i18n.t("components.products.productDetailsDialog.product"), type: "text" },
  "Parametric Collection": { label: i18n.t("components.products.productDetailsDialog.collection"), type: "text" },
  "Parametric Material": { label: i18n.t("components.products.productDetailsDialog.material"), type: "text" },

  // Color — kept specific over the Color Family rollup.
  "Parametric Color": { label: i18n.t("components.products.productDetailsDialog.color"), type: "text" },
  // "Parametric Color Family": { label: "Color Family", type: "text" },

  "EAN": { label: i18n.t("components.products.productDetailsDialog.eanGtin"), type: "text" },

  // ── Number fields (rendered in the grid with formatting) ──
  "Unit Weight": { label: i18n.t("components.products.productDetailsDialog.weightLb"), type: "number" },
  "Unit Cubes": { label: i18n.t("components.products.productDetailsDialog.volumeCuFt"), type: "number" },

  // ── Hidden fields (present in data but not shown) ──
  "Image URL - Primary": { label: i18n.t("components.products.productDetailsDialog.primaryImage"), type: "hidden" },
  "Thumbnail Image URL": { label: i18n.t("components.products.productDetailsDialog.thumbnailImage"), type: "hidden" },
  "UOM List": { label: i18n.t("components.products.productDetailsDialog.unitsOfMeasure"), type: "hidden" },
};

// ── Helper functions ─────────────────────────────────────────────────────────

function getFieldsByType(
  extraData: Record<string, string | boolean | number>,
  type: FieldType,
): Array<{ key: string; def: FieldDef; value: string | boolean | number }> {
  return Object.entries(extraDataFields)
    .filter(
      ([key, def]) =>
        def.type === type && key in extraData && extraData[key] != null,
    )
    .map(([key, def]) => ({ key, def, value: extraData[key] }));
}

function formatValue(
  value: string | boolean | number | null | undefined,
): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const s = value.toString();
    if (s.includes(".")) {
      return s.split(".")[0] + "." + s.split(".")[1].slice(0, 2);
    }
    return s;
  }
  return value;
}

// ── Component ────────────────────────────────────────────────────────────────

export const ProductDetailsDialog: React.FC<ProductDetailsDialogProps> = ({
  product,
  open,
  onOpenChange,
  quantity = 0,
  onQuantityChange,
  showCachedPricing = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const extraData = product.extraData as
    | Record<string, string | boolean | number>
    | undefined;

  const urlFields = extraData ? getFieldsByType(extraData, "url") : [];
  const textFields = extraData
    ? [
        ...getFieldsByType(extraData, "text"),
        ...getFieldsByType(extraData, "number"),
      ]
    : [];
  const listFields = extraData ? getFieldsByType(extraData, "list") : [];

  const basicInfoFields = [
    { label: t("components.products.productDetailsDialog.hajocaProductId"), value: product.hajocaProductId },
    { label: t("components.products.productDetailsDialog.sku"), value: product.sku || null },
    { label: t("components.products.productDetailsDialog.manufacturer"), value: product.manufacturer || null },
  ].filter(
    (f): f is { label: string; value: string } => f.value != null,
  );

  const hasDetails =
    urlFields.length > 0 ||
    textFields.length > 0 ||
    listFields.length > 0 ||
    basicInfoFields.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[600px] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {t("components.products.productDetailsDialog.productDetails")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Image */}
          <div className="flex justify-center">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.description}
                className="max-h-48 max-w-full object-contain rounded-lg border"
              />
            ) : (
              <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded-lg border">
                <Package className="h-16 w-16 text-gray-400" />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h3 className="font-semibold text-lg leading-snug">
              {product.description}
            </h3>
          </div>

          {/* Pricing */}
          <div className="pb-4 border-b border-gray-200">
            <p className="text-muted-foreground text-xs uppercase tracking-wide mb-2 font-medium">
              {t("components.products.productDetailsDialog.price")}
            </p>
            {product.currentPrice ? (
              <p className="text-3xl font-bold text-green-600">
                ${formatCentsAsDollars(product.currentPrice)}
                {product.unitOfMeasure && (
                  <span className="text-base font-normal text-muted-foreground ml-2">
                    / {formatUnitOfMeasure(product.unitOfMeasure)}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-xl font-semibold text-muted-foreground">
                {t("components.products.productDetailsDialog.callForPrice")}
              </p>
            )}
            {onQuantityChange && (
              <div className="mt-3">
                <ProductQuantityControls
                  product={product}
                  quantity={quantity}
                  onQuantityChange={onQuantityChange}
                  showCachedPricing={showCachedPricing}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* Smart Description */}
          {product.aiDescription && (
            <div className="pt-4">
              <p className="text-muted-foreground text-xs uppercase tracking-wide mb-3 font-medium">
                {t("components.products.productDetailsDialog.smartDescription")}
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm leading-relaxed text-gray-700">
                  {product.aiDescription}
                </p>
              </div>
            </div>
          )}

          {/* Product Details */}
          {hasDetails && (
            <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
              {/* Documents & Resources (URL fields as buttons) */}
              {urlFields.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-3 font-medium">
                    {t("components.products.productDetailsDialog.documentsResources")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {urlFields.map(({ key, def, value }) => {
                      const Icon = def.icon || ExternalLink;
                      return (
                        <a
                          key={key}
                          href={String(value)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors"
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {def.label}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Text & Number fields in grid */}
              {(textFields.length > 0 || basicInfoFields.length > 0) && (
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-3 font-medium">
                    {t("components.products.productDetailsDialog.productDetails")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-lg">
                    {textFields.map(({ key, def, value }) => (
                      <div key={key}>
                        <p className="text-muted-foreground">{def.label}</p>
                        <p className="font-medium">{formatValue(value)}</p>
                      </div>
                    ))}
                    {basicInfoFields.map((field) => (
                      <div key={field.label}>
                        <p className="text-muted-foreground">{field.label}</p>
                        <p className="font-medium">{field.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List fields below the grid */}
              {listFields.map(({ key, def, value }) => {
                const separator = def.separator || "|";
                const items = String(value)
                  .split(separator)
                  .map((s) => s.trim())
                  .filter(Boolean);
                if (items.length === 0) return null;
                return (
                  <div key={key}>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide mb-2 font-medium">
                      {def.label}
                    </p>
                    <ul className="text-sm bg-gray-50 p-4 rounded-lg space-y-1.5">
                      {items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-muted-foreground mt-1.5 h-1 w-1 rounded-full bg-gray-400 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
